import React, { useState, useMemo } from 'react';
import { Form, Dropdown } from 'react-bootstrap';
import {
  ShieldCheck, Lock, BrickWall, Monitor, Settings, DoorOpen, EyeOff, KeyRound, Fingerprint, UsersRound, Crown, ScrollText, FolderTree, Key, Search, Users, HardDrive, UserCog, DatabaseBackup, Clock, Boxes, Database, Server, AlertTriangle, Globe, Activity, FileText, Smartphone, Laptop, Cpu, Wifi, Crosshair, AlertOctagon, Terminal, Trash2, Home
} from 'lucide-react';

export const AVAILABLE_ICONS = {
  Search, ShieldCheck, Lock, BrickWall, Monitor, Settings, DoorOpen, EyeOff, KeyRound, Fingerprint, UsersRound, Crown, ScrollText, FolderTree, Key, Users, HardDrive, UserCog, DatabaseBackup, Clock, Boxes, Database, Server, AlertTriangle, Globe, Activity, FileText, Smartphone, Laptop, Cpu, Wifi, Crosshair, AlertOctagon, Terminal, Trash2, Home
};

const iconNames = Object.keys(AVAILABLE_ICONS).sort();

const CategoryIconPicker = ({ value, onChange, label = "Icon", required = false }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredIcons = useMemo(() => {
    if (!searchTerm) return iconNames;
    const lower = searchTerm.toLowerCase();
    return iconNames.filter(name => name.toLowerCase().includes(lower));
  }, [searchTerm]);

  const SelectedIcon = value && AVAILABLE_ICONS[value] ? AVAILABLE_ICONS[value] : null;

  return (
    <Form.Group className="mb-3">
      {label && <Form.Label className="fw-bold small mb-1">{label} {required && '*'}</Form.Label>}
      <Dropdown>
        <Dropdown.Toggle 
          variant="outline-secondary" 
          className="w-100 d-flex align-items-center justify-content-between text-start bg-white"
          style={{ padding: '0.45rem 0.75rem', borderRadius: '6px' }}
        >
          <div className="d-flex align-items-center gap-2">
            {SelectedIcon ? (
              <span className="text-primary bg-primary bg-opacity-10 rounded p-1 d-flex align-items-center justify-content-center">
                <SelectedIcon size={18} />
              </span>
            ) : (
              <span className="text-secondary d-flex align-items-center justify-content-center rounded p-1" style={{ border: '1.5px dashed #ced4da', width: 30, height: 30 }} />
            )}
            <span className={SelectedIcon ? 'fw-semibold' : 'text-muted fst-italic'}>
              {value || 'Select an icon...'}
            </span>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100 p-2 shadow-lg border-0" style={{ maxHeight: '320px', overflowY: 'auto' }}>
          <div className="position-sticky top-0 bg-white pb-2 z-1">
            <Form.Control
              type="text"
              placeholder="Search icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              className="mb-1"
              autoFocus
            />
          </div>
          
          {/* None / clear option */}
          {!required && (
            <Dropdown.Item 
              onClick={() => onChange(null)}
              className={`d-flex align-items-center gap-2 rounded px-2 py-1 mb-2 ${!value ? 'bg-secondary bg-opacity-10 fw-bold' : ''}`}
            >
              <span className="text-secondary d-flex align-items-center justify-content-center rounded" style={{ border: '1.5px dashed #ced4da', width: 26, height: 26 }} />
              <span className="small text-secondary fst-italic">None / Default</span>
            </Dropdown.Item>
          )}

          {/* Icon grid */}
          {filteredIcons.length === 0 ? (
            <div className="text-center text-muted small py-2 w-100">No icons found</div>
          ) : (
            <div className="d-flex flex-wrap gap-1">
              {filteredIcons.map(iconName => {
                const IconComp = AVAILABLE_ICONS[iconName];
                const isSelected = value === iconName;
                return (
                  <button
                    key={iconName}
                    type="button"
                    title={iconName}
                    onClick={() => onChange(iconName)}
                    className={`border-0 rounded d-flex flex-column align-items-center justify-content-center gap-1 p-2`}
                    style={{
                      width: 60,
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(13, 110, 253, 0.12)' : 'transparent',
                      outline: isSelected ? '2px solid rgba(13, 110, 253, 0.5)' : 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <IconComp size={22} color={isSelected ? '#0d6efd' : '#475569'} />
                    <span style={{ fontSize: '0.55rem', color: isSelected ? '#0d6efd' : '#64748b', fontWeight: isSelected ? 700 : 400, lineHeight: 1.2, textAlign: 'center', wordBreak: 'break-all' }}>
                      {iconName}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

export default CategoryIconPicker;
